// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useEffect, useState, type SyntheticEvent } from "react";

import {
  ArrowRight,
  CircleCheck,
  GitCompareArrows,
  GitPullRequest,
  LockKeyhole,
  ShieldCheck,
  SquareTerminal as TerminalWindow,
} from "lucide-react";

import {
  beginBrowserLogin,
  CockpitApi,
  hasBrowserCsrfCookie,
  inspectBrowserSession,
  saveIdentity,
  type CockpitIdentity,
  type CockpitScope,
} from "./api.js";

const DEFAULTS = {
  actorId: "11111111-1111-4111-8111-111111111111",
  organizationId: "22222222-2222-4222-8222-222222222222",
  projectId: "33333333-3333-4333-8333-333333333333",
};

export function Setup({
  initialAuthentication,
  initialScope,
  initialStatus,
  onReady,
}: {
  readonly initialAuthentication: CockpitIdentity["authentication"];
  readonly initialScope: CockpitScope | null;
  readonly initialStatus: string | null;
  readonly onReady: (identity: CockpitIdentity) => void;
}) {
  const [actorId, setActorId] = useState(DEFAULTS.actorId);
  const [authentication, setAuthentication] = useState<
    CockpitIdentity["authentication"]
  >(initialAuthentication);
  const [organizationId, setOrganizationId] = useState(
    initialScope?.organizationId ?? DEFAULTS.organizationId,
  );
  const [projectId, setProjectId] = useState(
    initialScope?.projectId ?? DEFAULTS.projectId,
  );
  const [organizationName, setOrganizationName] = useState("Diffclaw");
  const [projectName, setProjectName] = useState("First project");
  const [sessionReady, setSessionReady] = useState(false);
  const [status, setStatus] = useState<string | null>(initialStatus);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const location = new URL(window.location.href);
    const login = location.searchParams.get("login");
    if (login === "failed") {
      setAuthentication("session");
      setStatus(
        `GitHub sign-in did not complete (${location.searchParams.get("code") ?? "authentication.failed"}).`,
      );
      window.history.replaceState(
        null,
        "",
        `${location.pathname}${location.hash}`,
      );
      return;
    }
    if (login !== "completed" && !hasBrowserCsrfCookie()) {
      return;
    }
    setBusy(true);
    setAuthentication("session");
    setStatus("Restoring the protected browser session…");
    void inspectBrowserSession()
      .then((session) => {
        setActorId(session.actor.actorId);
        setOrganizationId(session.actor.organizationId);
        setSessionReady(true);
        setStatus(
          "GitHub session verified. Enter the Project ID to open its workspace.",
        );
      })
      .catch((error: unknown) => {
        setSessionReady(false);
        setStatus(
          error instanceof Error
            ? `The browser session is unavailable: ${error.message}`
            : "The browser session is unavailable.",
        );
      })
      .finally(() => {
        setBusy(false);
        window.history.replaceState(
          null,
          "",
          `${location.pathname}${location.hash}`,
        );
      });
  }, []);

  function identity(): CockpitIdentity {
    return { actorId, authentication, organizationId, projectId };
  }

  function save(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (authentication === "session" && !sessionReady) {
      setStatus("Verify a GitHub browser session before opening a Project.");
      return;
    }
    const value = identity();
    saveIdentity(value);
    onReady(value);
  }

  async function signIn() {
    setBusy(true);
    setStatus("Creating a one-use GitHub authorization challenge…");
    try {
      const challenge = await beginBrowserLogin(organizationId);
      setStatus("Redirecting to GitHub…");
      window.location.assign(challenge.authorizationUrl);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  }

  async function restoreSession() {
    setBusy(true);
    setStatus("Inspecting the protected browser session…");
    try {
      const session = await inspectBrowserSession();
      setActorId(session.actor.actorId);
      setOrganizationId(session.actor.organizationId);
      setSessionReady(true);
      setStatus(
        "GitHub session verified. Enter the Project ID to open its workspace.",
      );
    } catch (error) {
      setSessionReady(false);
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function initialize() {
    setBusy(true);
    setStatus("Creating the local Organization…");
    try {
      const api = new CockpitApi(identity());
      await api.command("bootstrapOrganization", {
        body: {
          name: organizationName,
          organizationId,
          ownerActorId: actorId,
        },
      });
      setStatus("Creating the Project and its initial policy…");
      await api.command("createProject", {
        body: { name: projectName, projectId },
      });
      const value = identity();
      saveIdentity(value);
      setStatus("Local Organization and Project are ready.");
      onReady(value);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="setup-shell" id="main-content">
      <section className="setup-story" aria-labelledby="setup-title">
        <div className="setup-brand">
          <span className="brand-mark" aria-hidden="true">
            <GitCompareArrows strokeWidth={2.2} />
          </span>
          Diffclaw
        </div>
        <div className="setup-story-copy">
          <h1 id="setup-title">Review the change, not the noise.</h1>
          <p>
            The intent, the exact code, what the repository already knows, and
            what nobody has checked yet. All in one place, before you sign off.
          </p>
        </div>
        <div
          className="setup-proof-list"
          aria-label="How Diffclaw handles your review"
        >
          <span>
            <ShieldCheck size={18} aria-hidden="true" />
            Every finding keeps the evidence it came from
          </span>
          <span>
            <LockKeyhole size={18} aria-hidden="true" />
            Your draft stays private until you publish it
          </span>
          <span>
            <CircleCheck size={18} aria-hidden="true" />
            What could not be checked is named, not hidden
          </span>
        </div>
      </section>

      <section className="setup-panel" aria-labelledby="workspace-title">
        <form onSubmit={save} className="setup-form">
          <h2 id="workspace-title">Open a review project</h2>
          <p>Sign in with the GitHub identity your installation trusts.</p>
          <label>
            Authentication
            <span className="select-with-icon">
              {authentication === "session" ? (
                <GitPullRequest size={17} aria-hidden="true" />
              ) : (
                <TerminalWindow size={17} aria-hidden="true" />
              )}
              <select
                value={authentication}
                onChange={(event) => {
                  setAuthentication(
                    event.target.value as CockpitIdentity["authentication"],
                  );
                  setSessionReady(false);
                }}
              >
                <option value="session">GitHub browser session</option>
                <option value="local-bootstrap">
                  Local development identity
                </option>
              </select>
            </span>
          </label>

          {authentication === "session" ? (
            <div
              className={
                sessionReady ? "session-card verified" : "session-card"
              }
            >
              <span className="session-card-icon" aria-hidden="true">
                {sessionReady ? (
                  <CircleCheck size={19} />
                ) : (
                  <GitPullRequest size={19} />
                )}
              </span>
              <span>
                <strong>
                  {sessionReady ? "GitHub identity verified" : "Connect GitHub"}
                </strong>
                <small>
                  {sessionReady
                    ? "You can open any project this identity can reach."
                    : "Diffclaw uses a protected browser session and never stores your GitHub password."}
                </small>
              </span>
              <button
                className={sessionReady ? "button secondary" : "button primary"}
                disabled={busy}
                onClick={() => void signIn()}
                type="button"
              >
                {sessionReady ? "Sign in again" : "Sign in with GitHub"}
              </button>
              {sessionReady ? null : (
                <button
                  className="text-button"
                  disabled={busy}
                  onClick={() => void restoreSession()}
                  type="button"
                >
                  Use current session
                </button>
              )}
            </div>
          ) : (
            <div className="development-note">
              <TerminalWindow size={18} aria-hidden="true" />
              <span>
                <strong>Local development only</strong>
                <small>
                  This path works on loopback with local bootstrap explicitly
                  enabled, and needs the identifiers below.
                </small>
              </span>
            </div>
          )}

          <div className="setup-scope-fields">
            <label>
              Organization ID
              <input
                className="code-input"
                required
                readOnly={authentication === "session" && sessionReady}
                value={organizationId}
                onChange={(event) => {
                  setOrganizationId(event.target.value);
                }}
              />
            </label>
            <label>
              Project ID
              <input
                className="code-input"
                required
                value={projectId}
                onChange={(event) => {
                  setProjectId(event.target.value);
                }}
              />
            </label>
            {authentication === "local-bootstrap" ? (
              <label>
                Actor ID
                <input
                  className="code-input"
                  required
                  value={actorId}
                  onChange={(event) => {
                    setActorId(event.target.value);
                  }}
                />
              </label>
            ) : null}
          </div>

          <button
            className="button primary"
            disabled={busy || (authentication === "session" && !sessionReady)}
            type="submit"
          >
            Open existing Project
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </form>

        {authentication === "local-bootstrap" ? (
          <details className="bootstrap-panel">
            <summary>Create a new local project instead</summary>
            <div className="form-grid">
              <label>
                Organization name
                <input
                  value={organizationName}
                  onChange={(event) => {
                    setOrganizationName(event.target.value);
                  }}
                />
              </label>
              <label>
                Project name
                <input
                  value={projectName}
                  onChange={(event) => {
                    setProjectName(event.target.value);
                  }}
                />
              </label>
              <button
                className="button secondary wide"
                disabled={busy}
                onClick={() => void initialize()}
                type="button"
              >
                Create organization and project
              </button>
            </div>
          </details>
        ) : null}

        {status === null ? null : (
          <p className="status-line" aria-live="polite">
            {status}
          </p>
        )}
      </section>
    </main>
  );
}
